import { normalizeLanguageCode } from '@common/language';
import { iframeLocalStorage } from './iframeLocalStorage';

/**
 * Best-effort read of the respondent's current language code, used by
 * translation helpers that need to pick the right localised string.
 *
 * Resolution order:
 *   1. `current_project_language_code{suffix}` in iframeLocalStorage —
 *      set by ConsentScreen once the respondent confirms their language.
 *   2. `navigator.language`, normalized through the shared locale table
 *      (e.g. "fr-FR" → "fr", "zh-Hant-TW" → "zh-TW"), if available on the
 *      current platform.
 *   3. Falls back to `'en'`.
 *
 * Pass `projectStorageSuffix` when the consumer has it (hooks inside
 * the chat loop already receive the suffix). Omit it for surfaces
 * rendered before the respondent has a project context (error
 * boundary, redirected-error page).
 */
export function getLanguageCode(projectStorageSuffix = ''): string {
  const stored = iframeLocalStorage.getItem(
    `current_project_language_code${projectStorageSuffix}`,
  );
  if (stored) return stored;

  if (typeof navigator !== 'undefined' && navigator.language) {
    // TELV1-538 — normalize through the shared locale table rather than
    // splitting on `-`. The previous exact-match special case caught only the
    // literal string `zh-TW`, so `zh-Hant-TW` and `zh-HK` still degraded to
    // `zh` (Simplified), and `he-IL`/`nb-NO`/`fil-PH` degraded to codes that
    // aren't translation keys at all and silently rendered English.
    //
    // This matters most on the embed / `skipConsent` path, where the stored
    // language can be empty and `navigator.language` is the effective source
    // for all chat chrome.
    return normalizeLanguageCode(navigator.language) ?? 'en';
  }

  return 'en';
}
